import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, array, object, string } from 'yup'

import { AutoEnrollmentModel } from '@/AutoEnrollment/domain'
import { Step2Form } from '@/Contract/ui/atoms'
import { documentType, iban } from '@/Shared/domain'
import {
  checkboxSchema,
  dniOrCifSchema,
  documentNumberSchema,
  documentTypeSchema,
  emailSchema,
  ibanSchema,
  mobilePhoneSchema,
  nonEmptyPositiveNumberSchema,
  nonEmptyStringSchema,
  phoneSchema,
  stringSchema,
  urlSchema,
} from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useUserData } from '@/User/ui/context'

import { AutoEnrollmentTranslation } from '../autoEnrollmentTranslation'
import { tpvSchema } from '@/Shared/ui/form/validations/tpvSchema'

export interface Step1Form {
  cif?: string
  businessName: string
  documentType: documentType
  documentNumber: string
  billingName: string
  billingSurname1: string
  billingSurname2?: string
  billingEmail: string
  billingPhone: string
  billingStreet: string
  billingNumber: string
  billingAdditionalInfo?: string
  billingFloorBlockStair?: string
  billingPostalCode: string
  billingCity: string
  billingProvince: string

  iban: string
  paymentName: string
  paymentSurname1: string
  paymentSurname2?: string
  paymentEmail: string
  paymentPhone: string
}

const step1Schema = (t: TFunction<'translation', undefined>): ObjectSchema<Step1Form> => {
  return object({
    cif: dniOrCifSchema(t),
    businessName: nonEmptyStringSchema(t),
    documentType: documentTypeSchema(t, true).required(
      t(formTranslation.errors.fieldRequired),
    ),
    documentNumber: documentNumberSchema(t, true, 'documentType').required(
      t(formTranslation.errors.fieldRequired),
    ),
    billingName: nonEmptyStringSchema(t, 20),
    billingSurname1: nonEmptyStringSchema(t, 20),
    billingSurname2: stringSchema(t, 20),
    billingEmail: emailSchema(t).required(t(formTranslation.errors.fieldRequired)),
    billingPhone: mobilePhoneSchema(t).required(t(formTranslation.errors.fieldRequired)),
    billingStreet: nonEmptyStringSchema(t),
    billingNumber: nonEmptyStringSchema(t),
    billingAdditionalInfo: stringSchema(t),
    billingFloorBlockStair: stringSchema(t),
    billingPostalCode: nonEmptyStringSchema(t),
    billingCity: nonEmptyStringSchema(t),
    billingProvince: nonEmptyStringSchema(t),

    iban: ibanSchema(t),
    paymentName: nonEmptyStringSchema(t, 20),
    paymentSurname1: nonEmptyStringSchema(t, 20),
    paymentSurname2: stringSchema(t, 20),
    paymentEmail: emailSchema(t).required(t(formTranslation.errors.fieldRequired)),
    paymentPhone: phoneSchema(t).required(t(formTranslation.errors.fieldRequired)),
  })
}

export interface Step2Form {
  storeName: string
  storeEmail: string
  storePhone?: string
  storeWeb?: string
  storeFoodTypeId: number | null
  storeFoodType?: string
  storeIsOnlineStore?: boolean
  storeHasHomeDelivery?: boolean
  storeHasLocalPickup?: boolean
  service?: string

  storeStreet: string
  storeNumber: string
  storeAdditionalInfo?: string
  storeFloorBlockStair?: string
  storePostalCode: string
  storeCity: string
  storeProvince: string
  storeLatitude: string
  storeLongitude: string

  contactName: string
  contactSurname1: string
  contactSurname2?: string
  contactEmail?: string
  contactPhone?: string

  terminalMid?: { id: string; value: string }[]

  useStoreAddressForWelcomePack: string
}

const step2Schema = (t: TFunction<'translation', undefined>): ObjectSchema<Step2Form> => {
  return object({
    storeName: nonEmptyStringSchema(t),
    storeEmail: emailSchema(t, true).required(t(formTranslation.errors.fieldRequired)),
    storePhone: phoneSchema(t).required(t(formTranslation.errors.fieldRequired)),
    storeWeb: urlSchema(t, false),
    storeFoodTypeId: nonEmptyPositiveNumberSchema(t),
    storeFoodType: string(),

    storeIsOnlineStore: checkboxSchema(t),
    storeHasHomeDelivery: checkboxSchema(t),
    storeHasLocalPickup: checkboxSchema(t),
    service: string(),

    storeStreet: nonEmptyStringSchema(t),
    storeNumber: nonEmptyStringSchema(t),
    storeAdditionalInfo: stringSchema(t),
    storeFloorBlockStair: stringSchema(t),
    storePostalCode: nonEmptyStringSchema(t),
    storeCity: nonEmptyStringSchema(t),
    storeProvince: nonEmptyStringSchema(t),
    storeLatitude: nonEmptyStringSchema(t),
    storeLongitude: nonEmptyStringSchema(t),

    contactName: nonEmptyStringSchema(t, 20),
    contactSurname1: nonEmptyStringSchema(t, 20),
    contactSurname2: stringSchema(t, 20),
    contactEmail: emailSchema(t),
    contactPhone: phoneSchema(t),

    terminalMid: array()
      .of(
        object({
          id: string().required(t(formTranslation.errors.fieldRequired)),
          value: tpvSchema(t),
        }),
      )
      .min(1, t(formTranslation.errors.atLeastOne))
      .optional(),

    useStoreAddressForWelcomePack: nonEmptyStringSchema(t),
  })
}

export type Step3ProductForm = {
  productId: number
  productServices?: number[]
}

export interface Step3Form {
  processType: number
  products: Step3ProductForm[]
  isIAERestaurant: boolean
}

const step3Schema = (t: TFunction<'translation', undefined>): ObjectSchema<Step3Form> => {
  return object({
    processType: nonEmptyPositiveNumberSchema(t),
    products: array()
      .of(
        object({
          productId: nonEmptyPositiveNumberSchema(t),
          productServices: array().of(nonEmptyPositiveNumberSchema(t)).optional(),
        }),
      )
      .required(t(formTranslation.errors.fieldRequired)),
    isIAERestaurant: checkboxSchema(t)
      .test(
        'must-be-accepted',
        t(AutoEnrollmentTranslation.productsConditions.signDocuments.acceptIAE.error),
        value => value === true,
      )
      .required(t(formTranslation.errors.fieldRequired)),
  })
}

export enum FillWithOwnerCases {
  payment = 'payment',
  manager = 'manager',
}

export type FormReturnType =
  | UseFormReturn<Step1Form>
  | UseFormReturn<Step2Form>
  | UseFormReturn<Step3Form>

interface IUseAutoEnrollmentFormManager {
  getCurrentForm: (step: number) => FormReturnType
  initForms: (autoEnrollmentData: AutoEnrollmentModel) => void
  fillWithOwnerData: (fillCase: FillWithOwnerCases) => void
}

export const useAutoEnrollmentFormManager = (): IUseAutoEnrollmentFormManager => {
  const {
    user: { user },
  } = useUserData()
  const { t } = useTranslation()

  const step1Form = useForm<Step1Form>({
    resolver: yupResolver(step1Schema(t)),
    mode: 'onChange',
  })

  const initStep1Form = (autoEnrollmentData: AutoEnrollmentModel): void => {
    step1Form.reset({
      ...step1Form.getValues(),
      cif: autoEnrollmentData.billingGeneralData.cif ?? '',
      businessName: autoEnrollmentData.billingGeneralData.businessName ?? '',
      documentType:
        autoEnrollmentData.billingOwnerData.documentType === documentType.Default
          ? undefined
          : autoEnrollmentData.billingOwnerData.documentType,
      documentNumber: autoEnrollmentData.billingOwnerData.documentNumber ?? '',
      billingName:
        autoEnrollmentData.billingOwnerData.name !== ''
          ? autoEnrollmentData.billingOwnerData.name
          : user?.name,
      billingSurname1:
        autoEnrollmentData.billingOwnerData.surname1 !== ''
          ? autoEnrollmentData.billingOwnerData.surname1
          : user?.surname,
      billingSurname2:
        autoEnrollmentData.billingOwnerData.surname2 &&
        autoEnrollmentData.billingOwnerData.surname2 !== ''
          ? autoEnrollmentData.billingOwnerData.surname2
          : user?.surname2 && user?.surname2 !== ''
            ? user.surname2
            : '',
      billingEmail:
        autoEnrollmentData.billingOwnerData.email !== ''
          ? autoEnrollmentData.billingOwnerData.email
          : user?.email,
      billingPhone: autoEnrollmentData.billingOwnerData.phone ?? '',
      billingStreet: autoEnrollmentData.billingAddressData.street ?? '',
      billingNumber: autoEnrollmentData.billingAddressData.number ?? '',
      billingAdditionalInfo: autoEnrollmentData.billingAddressData.additionalInfo ?? '',
      billingFloorBlockStair: autoEnrollmentData.billingAddressData.floorBlockStair ?? '',
      billingPostalCode: autoEnrollmentData.billingAddressData.postalCode ?? '',
      billingCity: autoEnrollmentData.billingAddressData.city ?? '',
      billingProvince: autoEnrollmentData.billingAddressData.province ?? '',

      iban: iban(autoEnrollmentData.paymentAccountData.iban ?? ''),
      paymentName: autoEnrollmentData.paymentContactData.name ?? '',
      paymentSurname1: autoEnrollmentData.paymentContactData.surname1 ?? '',
      paymentSurname2: autoEnrollmentData.paymentContactData.surname2 ?? '',
      paymentEmail: autoEnrollmentData.paymentContactData.email ?? '',
      paymentPhone: autoEnrollmentData.paymentContactData.phone ?? '',
    })
  }

  const step2Form = useForm<Step2Form>({
    resolver: yupResolver(step2Schema(t)),
    mode: 'onChange',
  })

  const initStep2Form = (autoEnrollmentData: AutoEnrollmentModel): void => {
    step2Form.reset({
      ...step2Form.getValues(),
      storeName: autoEnrollmentData.storeGeneralData.name ?? '',
      storeEmail: autoEnrollmentData.storeGeneralData.email ?? '',
      storePhone: autoEnrollmentData.storeGeneralData.phone ?? '',
      storeWeb: autoEnrollmentData.storeGeneralData.url ?? '',
      storeFoodTypeId: autoEnrollmentData.storeGeneralData.food.id ?? undefined,
      storeFoodType: autoEnrollmentData.storeGeneralData.food.name ?? '',
      storeIsOnlineStore: !autoEnrollmentData.storeGeneralData.service.physical,
      storeHasHomeDelivery: !!autoEnrollmentData.storeGeneralData.service.delivery,
      storeHasLocalPickup: !!autoEnrollmentData.storeGeneralData.service.takeAway,
      service: '',
      storeStreet: autoEnrollmentData.storeAddressData.street ?? '',
      storeNumber: autoEnrollmentData.storeAddressData.number ?? '',
      storeAdditionalInfo: autoEnrollmentData.storeAddressData.additionalInfo ?? '',
      storeFloorBlockStair: autoEnrollmentData.storeAddressData.floorBlockStair ?? '',
      storePostalCode: autoEnrollmentData.storeAddressData.postalCode ?? '',
      storeCity: autoEnrollmentData.storeAddressData.city ?? '',
      storeProvince: autoEnrollmentData.storeAddressData.province ?? '',
      storeLatitude: autoEnrollmentData.storeAddressData.latitude ?? '',
      storeLongitude: autoEnrollmentData.storeAddressData.longitude ?? '',
      contactName: autoEnrollmentData.storeContactData.name ?? '',
      contactSurname1: autoEnrollmentData.storeContactData.surname1 ?? '',
      contactSurname2: autoEnrollmentData.storeContactData.surname2 ?? '',
      contactEmail: autoEnrollmentData.storeContactData.email ?? '',
      contactPhone: autoEnrollmentData.storeContactData.phone ?? '',
      terminalMid: autoEnrollmentData.tpvData.terminalMid?.length
        ? autoEnrollmentData.tpvData.terminalMid.map((tpv, index) => {
            return {
              id: index.toString(),
              value: tpv,
            }
          })
        : [{ id: '0', value: '' }],
      useStoreAddressForWelcomePack:
        autoEnrollmentData.welcomePackData.useStoreAddress?.toString() ?? true.toString(),
    })
  }

  const step3Form = useForm<Step3Form>({
    resolver: yupResolver(step3Schema(t)),
    mode: 'onChange',
  })

  const initStep3Form = (autoEnrollmentData: AutoEnrollmentModel): void => {
    step3Form.reset({
      ...step3Form.getValues(),
      processType: autoEnrollmentData.processType ?? 1,
      isIAERestaurant: false,
      products: autoEnrollmentData.products,
    })
  }

  const fillWithOwnerData = (fillCase: FillWithOwnerCases): void => {
    switch (fillCase) {
      case FillWithOwnerCases.payment:
        step1Form.setValue('paymentName', step1Form.getValues('billingName'))
        step1Form.trigger('paymentName')
        step1Form.setValue('paymentSurname1', step1Form.getValues('billingSurname1'))
        step1Form.trigger('paymentSurname1')
        step1Form.setValue('paymentSurname2', step1Form.getValues('billingSurname2'))
        step1Form.trigger('paymentSurname2')
        step1Form.setValue('paymentEmail', step1Form.getValues('billingEmail'))
        step1Form.trigger('paymentEmail')
        step1Form.setValue('paymentPhone', step1Form.getValues('billingPhone'))
        step1Form.trigger('paymentPhone')
        break
      case FillWithOwnerCases.manager:
        step2Form.setValue('contactName', step1Form.getValues('billingName'))
        step2Form.trigger('contactName')
        step2Form.setValue('contactSurname1', step1Form.getValues('billingSurname1'))
        step2Form.trigger('contactSurname1')
        step2Form.setValue('contactSurname2', step1Form.getValues('billingSurname2'))
        step2Form.trigger('contactSurname2')
        step2Form.setValue('contactEmail', step1Form.getValues('billingEmail'))
        step2Form.trigger('contactEmail')
        step2Form.setValue('contactPhone', step1Form.getValues('billingPhone'))
        step2Form.trigger('contactPhone')
        break
    }
  }

  const getCurrentForm = (step: number): FormReturnType => {
    switch (step) {
      case 1:
        return step1Form
      case 2:
        return step2Form
      case 3:
        return step3Form
      default:
        return step1Form
    }
  }

  const initForms = (autoEnrollmentData: AutoEnrollmentModel): void => {
    initStep1Form(autoEnrollmentData)
    initStep2Form(autoEnrollmentData)
    initStep3Form(autoEnrollmentData)
  }

  return {
    getCurrentForm,
    initForms,
    fillWithOwnerData,
  }
}
